import { supabaseClient } from '../../services/supabase';

export interface EnergySeriesResult {
  chartData: any;
  chartOptions: any;
  latestYear?: number;
  latestValue?: number;
}

type Sector = 'residential' | 'commercial';

// Sector-specific color shades (dark=observed, medium=interpolated, light=extrapolated)
const sectorColors: Record<Sector, { observed: string; interpolated: string; extrapolated: string }> = {
  residential: {
    observed: '#1e40af',      // Dark blue
    interpolated: '#3b82f6',  // Medium blue
    extrapolated: '#60a5fa'   // Light blue
  },
  commercial: {
    observed: '#166534',      // Dark green
    interpolated: '#22c55e',  // Medium green
    extrapolated: '#4ade80'   // Light green
  }
};

const commonOptions = (title: string, yLabel: string, stacked = false) => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { 
      position: 'bottom',
      labels: {
        padding: 10,
        usePointStyle: true,
        pointStyle: 'rect'
      }
    },
    title: { display: true, text: title },
    tooltip: {
      callbacks: {
        label: (ctx: any) => {
          const v = Number(ctx.parsed.y);
          return `${ctx.dataset.label}: ${Number.isFinite(v) ? v.toLocaleString() : v}`;
        }
      }
    }
  },
  scales: {
    x: { stacked, title: { display: true, text: 'Year' } },
    y: { stacked, beginAtZero: true, title: { display: true, text: yLabel }, ticks: { callback: (v: any) => Number(v).toLocaleString() } }
  },
  layout: {
    padding: {
      bottom: 20
    }
  }
});

export async function fetchElectricitySeries(location: string, sector: Sector): Promise<EnergySeriesResult> {
  // Use the new format tables (elec_res_format, elec_com_format)
  const table = sector === 'residential' ? 'elec_res_format' : 'elec_com_format';
  const years = Array.from({ length: 20 }, (_, i) => 2005 + i);
  
  try {
    // Get geofips for the location
    const { data: locationData, error: locationError } = await supabaseClient
      .from('population')
      .select('geofips, location_name, geography_type')
      .eq('location_name', location)
      .limit(1)
      .single();

    if (locationError || !locationData) {
      console.error('Error fetching location geofips:', locationError);
      throw new Error(`Could not find location: ${location}`);
    }

    const rawGeofips = String(locationData.geofips).replace(/^0+/, ''); // Remove leading zeros
    
    const denomColumn = sector === 'residential' ? 'population' : 'total_worker';
    const sectorLabel = sector === 'residential' ? 'Residential' : 'Commercial';

    // Fetch data from new format table
    const { data: energyData, error: energyError } = await supabaseClient
      .from(table)
      .select(`year, total_kwh, ${denomColumn}, measurement, method_note`)
      .eq('geofips', rawGeofips)
      .eq('sector', sectorLabel)
      .in('year', years)
      .order('year', { ascending: true });

    if (energyError) {
      console.error('Error fetching energy data:', energyError);
      throw energyError;
    }

    // Fetch statewide data for elasticity-by-year normalization
    const { data: stateEnergyData, error: stateEnergyError } = await supabaseClient
      .from(table)
      .select(`year, total_kwh, ${denomColumn}, measurement, method_note`)
      .eq('geofips', '6')
      .eq('sector', sectorLabel)
      .in('year', years)
      .order('year', { ascending: true });

    if (stateEnergyError) {
      console.warn('Error fetching statewide energy data:', stateEnergyError);
    }

    const observedMap = new Map<number, number>();
    const denomMap = new Map<number, number>();
    const allValueMap = new Map<number, number>();

    (energyData || []).forEach((row: any) => {
      const year = row.year;
      const value = parseFloat(row.total_kwh);
      const denomValue = parseFloat(row[denomColumn]);

      if (!isNaN(value) && value >= 0) {
        let measurement = (row.measurement || 'observed').toLowerCase().trim();
        const methodNote = (row.method_note || '').toLowerCase().trim();

        if (methodNote.includes('extrapol')) {
          measurement = 'estimated';
        } else if (methodNote.includes('interpol')) {
          measurement = 'interpolated';
        }

        if (measurement === 'observed' && !observedMap.has(year)) {
          observedMap.set(year, value);
        }

        if (!allValueMap.has(year)) {
          allValueMap.set(year, value);
        }
      }

      if (!isNaN(denomValue) && denomValue > 0) {
        denomMap.set(year, denomValue);
      }
    });

    // Build statewide elasticity by year
    const stateValueMap = new Map<number, number>();
    const stateDenomMap = new Map<number, number>();

    (stateEnergyData || []).forEach((row: any) => {
      const year = row.year;
      const value = parseFloat(row.total_kwh);
      const denomValue = parseFloat(row[denomColumn]);
      if (!isNaN(value) && value > 0) {
        const measurement = (row.measurement || 'observed').toLowerCase().trim();
        const methodNote = (row.method_note || '').toLowerCase().trim();
        const isObserved = measurement === 'observed' && !methodNote.includes('extrapol');

        if (isObserved || !stateValueMap.has(year)) {
          stateValueMap.set(year, value);
        }
      }
      if (!isNaN(denomValue) && denomValue > 0) {
        if (!stateDenomMap.has(year)) {
          stateDenomMap.set(year, denomValue);
        }
      }
    });

    const stateElasticityByYear = new Map<number, number>();
    years.forEach((year) => {
      const prevYear = year - 1;
      const k0 = stateValueMap.get(prevYear);
      const k1 = stateValueMap.get(year);
      const d0 = stateDenomMap.get(prevYear);
      const d1 = stateDenomMap.get(year);
      if (!k0 || !k1 || !d0 || !d1) return;
      if (k0 <= 0 || k1 <= 0 || d0 <= 0 || d1 <= 0) return;
      const logK = Math.log(k1 / k0);
      const logD = Math.log(d1 / d0);
      if (!Number.isFinite(logK) || !Number.isFinite(logD) || logD === 0) return;
      stateElasticityByYear.set(year, logK / logD);
    });

    const observedYears = Array.from(observedMap.keys()).sort((a, b) => a - b);
    const interpolatedMap = new Map<number, number>();
    for (let i = 1; i < observedYears.length; i += 1) {
      const y0 = observedYears[i - 1];
      const y1 = observedYears[i];
      const v0 = observedMap.get(y0);
      const v1 = observedMap.get(y1);
      if (v0 === undefined || v1 === undefined) continue;
      const span = y1 - y0;
      if (span <= 1) continue;
      for (let y = y0 + 1; y < y1; y += 1) {
        const frac = (y - y0) / span;
        interpolatedMap.set(y, v0 + (v1 - v0) * frac);
      }
    }

    let predictedMap = new Map<number, number>();
    let scale = 1;
    if (sector === 'commercial') {
      // Build predicted series using statewide elasticity by year
      const baseYear = observedYears.length ? observedYears[0] : undefined;
      if (baseYear !== undefined) {
        predictedMap.set(baseYear, observedMap.get(baseYear) as number);
      } else {
        const fallbackYear = years.find((y) => allValueMap.has(y));
        if (fallbackYear !== undefined) {
          predictedMap.set(fallbackYear, allValueMap.get(fallbackYear) as number);
        }
      }

      years.forEach((year) => {
        if (predictedMap.has(year)) return;
        const prevYear = year - 1;
        const prevValue = predictedMap.get(prevYear);
        const d0 = denomMap.get(prevYear);
        const d1 = denomMap.get(year);
        const elasticity = stateElasticityByYear.get(year);
        if (!prevValue || !d0 || !d1 || !elasticity) return;
        const ratio = d1 / d0;
        if (!Number.isFinite(ratio) || ratio <= 0) return;
        predictedMap.set(year, prevValue * Math.pow(ratio, elasticity));
      });

      [...years].reverse().forEach((year) => {
        if (predictedMap.has(year)) return;
        const nextYear = year + 1;
        const nextValue = predictedMap.get(nextYear);
        const d0 = denomMap.get(year);
        const d1 = denomMap.get(nextYear);
        const elasticity = stateElasticityByYear.get(nextYear);
        if (!nextValue || !d0 || !d1 || !elasticity) return;
        const ratio = d1 / d0;
        if (!Number.isFinite(ratio) || ratio <= 0) return;
        predictedMap.set(year, nextValue / Math.pow(ratio, elasticity));
      });

      // Normalize predicted series to observed values without overwriting observed years
      const ratioList: number[] = [];
      observedMap.forEach((observedValue, year) => {
        const predictedValue = predictedMap.get(year);
        if (predictedValue && predictedValue > 0) {
          ratioList.push(observedValue / predictedValue);
        }
      });
      scale =
        ratioList.length > 0
          ? ratioList.sort((a, b) => a - b)[Math.floor(ratioList.length / 2)]
          : 1;
    }

    let avgPerResident = 0;
    if (sector === 'residential') {
      const ratios: number[] = [];
      observedYears.forEach((year) => {
        const value = observedMap.get(year);
        const denomValue = denomMap.get(year);
        if (value !== undefined && denomValue && denomValue > 0) {
          ratios.push(value / denomValue);
        }
      });

      if (ratios.length === 0) {
        const fallbackYear = 2016;
        const fallbackValue = allValueMap.get(fallbackYear);
        const fallbackDenom = denomMap.get(fallbackYear);
        if (fallbackValue !== undefined && fallbackDenom && fallbackDenom > 0) {
          ratios.push(fallbackValue / fallbackDenom);
        }
      }

      if (ratios.length === 0) {
        years.some((year) => {
          const value = allValueMap.get(year);
          const denomValue = denomMap.get(year);
          if (value !== undefined && denomValue && denomValue > 0) {
            ratios.push(value / denomValue);
            return true;
          }
          return false;
        });
      }

      avgPerResident = ratios.length
        ? ratios.reduce((sum, v) => sum + v, 0) / ratios.length
        : 0;
    }

    // Process data into observed, interpolated, extrapolated arrays
    const observedData: number[] = [];
    const interpolatedData: number[] = [];
    const extrapolatedData: number[] = [];
    
    // Build arrays for all years
    years.forEach(year => {
      if (observedMap.has(year)) {
        observedData.push(observedMap.get(year) as number);
        interpolatedData.push(0);
        extrapolatedData.push(0);
        return;
      }

      if (interpolatedMap.has(year)) {
        observedData.push(0);
        interpolatedData.push(interpolatedMap.get(year) as number);
        extrapolatedData.push(0);
        return;
      }

      if (sector === 'residential') {
        const denomValue = denomMap.get(year);
        if (avgPerResident > 0 && denomValue && denomValue > 0) {
          observedData.push(0);
          interpolatedData.push(0);
          extrapolatedData.push(avgPerResident * denomValue);
          return;
        }
      } else {
        const predictedValue = predictedMap.get(year);
        if (predictedValue && Number.isFinite(predictedValue)) {
          observedData.push(0);
          interpolatedData.push(0);
          extrapolatedData.push(predictedValue * scale);
          return;
        }
      }

      observedData.push(0);
      interpolatedData.push(0);
      extrapolatedData.push(0);
    });
    
    const chartData = {
      labels: years,
      datasets: [
        { 
          type: 'bar', 
          label: 'Observed', 
          data: observedData, 
          backgroundColor: sectorColors[sector].observed,
          borderColor: '#374151',
          borderWidth: 1
        },
        { 
          type: 'bar', 
          label: 'Interpolated', 
          data: interpolatedData, 
          backgroundColor: sectorColors[sector].interpolated,
          borderColor: '#374151',
          borderWidth: 1
        },
        { 
          type: 'bar', 
          label: 'Extrapolated', 
          data: extrapolatedData, 
          backgroundColor: sectorColors[sector].extrapolated,
          borderColor: '#374151',
          borderWidth: 1
        }
      ]
    };
    
    const latestYear = years.length ? years[years.length - 1] : undefined;
    const latestValue = observedData.length ? 
      (observedData[observedData.length - 1] + interpolatedData[interpolatedData.length - 1] + extrapolatedData[extrapolatedData.length - 1]) : 
      undefined;
    
    return {
      chartData,
      chartOptions: commonOptions(`${sector === 'residential' ? 'Residential' : 'Commercial'} Electricity Consumption (kWh)`, 'kWh', true),
      latestYear,
      latestValue
    };
    
  } catch (error) {
    console.error(`Error fetching electricity data for ${location}:`, error);
    throw error;
  }
}

export async function fetchElectricityStacked(location: string): Promise<EnergySeriesResult> {
  const [res, com] = await Promise.all([
    fetchElectricitySeries(location, 'residential'),
    fetchElectricitySeries(location, 'commercial')
  ]);
  const labels: number[] = Array.from(new Set([...(res.chartData.labels || []), ...(com.chartData.labels || [])])).sort();

  // Create maps for each series
  const resMap = new Map((res.chartData.labels || []).map((y: number, i: number) => [y, (res.chartData.datasets?.[0]?.data?.[i] ?? 0) as number]));
  const comMap = new Map((com.chartData.labels || []).map((y: number, i: number) => [y, (com.chartData.datasets?.[0]?.data?.[i] ?? 0) as number]));

  const resSeries = labels.map((y) => (resMap.has(y) ? (resMap.get(y) as number) : 0));
  const comSeries = labels.map((y) => (comMap.has(y) ? (comMap.get(y) as number) : 0));

  return {
    chartData: {
      labels,
      datasets: [
        { type: 'bar', label: 'Residential Electricity', data: resSeries, backgroundColor: sectorColors.residential.observed },
        { type: 'bar', label: 'Commercial Electricity', data: comSeries, backgroundColor: sectorColors.commercial.observed }
      ]
    },
    chartOptions: commonOptions('Total Electricity Consumption (kWh)', 'kWh', true)
  };
}

export async function fetchNaturalGasSeries(location: string, sector: Sector): Promise<EnergySeriesResult> {
  // Use the new format tables (ng_res_format, ng_com_format)
  const table = sector === 'residential' ? 'ng_res_format' : 'ng_com_format';
  const years = Array.from({ length: 20 }, (_, i) => 2005 + i);
  
  try {
    // Get geofips for the location
    const { data: locationData, error: locationError } = await supabaseClient
      .from('population')
      .select('geofips, location_name, geography_type')
      .eq('location_name', location)
      .limit(1)
      .single();

    if (locationError || !locationData) {
      console.error('Error fetching location geofips:', locationError);
      throw new Error(`Could not find location: ${location}`);
    }

    const rawGeofips = String(locationData.geofips).replace(/^0+/, ''); // Remove leading zeros
    
    // Fetch data from new format table
    const { data: energyData, error: energyError } = await supabaseClient
      .from(table)
      .select('year, total_therms, measurement, method_note')
      .eq('geofips', rawGeofips)
      .eq('sector', sector === 'residential' ? 'Residential' : 'Commercial')
      .in('year', years)
      .order('year', { ascending: true });

    if (energyError) {
      console.error('Error fetching energy data:', energyError);
      throw energyError;
    }

    // Process data into observed, interpolated, extrapolated arrays
    const observedData: number[] = [];
    const interpolatedData: number[] = [];
    const extrapolatedData: number[] = [];
    
    const dataMap = new Map<number, { value: number; measurement: string }>();
    
    (energyData || []).forEach((row: any) => {
      const year = row.year;
      const value = parseFloat(row.total_therms) || 0;
      const measurement = row.measurement || '';
      const methodNote = row.method_note || '';
      
      // Determine source type based on measurement and method_note
      let source = 'observed';
      if (measurement.toLowerCase().includes('interpolated') || methodNote.toLowerCase().includes('interpol')) {
        source = 'interpolated';
      } else if (measurement.toLowerCase().includes('extrapolated') || measurement.toLowerCase().includes('estimated') || methodNote.toLowerCase().includes('extrapol')) {
        source = 'extrapolated';
      }
      
      // Keep the last value if duplicates exist
      if (!dataMap.has(year) || source === 'observed') {
        dataMap.set(year, { value, measurement: source });
      }
    });
    
    years.forEach((year) => {
      const entry = dataMap.get(year);
      const value = entry ? entry.value : 0;
      const source = entry ? entry.measurement : 'extrapolated';
      
      // Initialize all arrays with zeros for this year
      observedData.push(0);
      interpolatedData.push(0);
      extrapolatedData.push(0);
      
      // Put the actual value in the appropriate dataset
      if (source === 'observed') {
        observedData[observedData.length - 1] = value;
      } else if (source === 'interpolated') {
        interpolatedData[interpolatedData.length - 1] = value;
      } else {
        extrapolatedData[extrapolatedData.length - 1] = value;
      }
    });
    
    const chartDataGas = {
      labels: years,
      datasets: [
        { 
          type: 'bar', 
          label: 'Observed', 
          data: observedData, 
          backgroundColor: sectorColors[sector].observed,
          borderColor: '#374151',
          borderWidth: 1
        },
        { 
          type: 'bar', 
          label: 'Interpolated', 
          data: interpolatedData, 
          backgroundColor: sectorColors[sector].interpolated,
          borderColor: '#374151',
          borderWidth: 1
        },
        { 
          type: 'bar', 
          label: 'Extrapolated', 
          data: extrapolatedData, 
          backgroundColor: sectorColors[sector].extrapolated,
          borderColor: '#374151',
          borderWidth: 1
        }
      ]
    };
    
    const latestYear = years.length ? years[years.length - 1] : undefined;
    const latestValue = observedData.length ? 
      (observedData[observedData.length - 1] || interpolatedData[interpolatedData.length - 1] || extrapolatedData[extrapolatedData.length - 1] || 0) : 
      undefined;
    
    return {
      chartData: chartDataGas,
      chartOptions: commonOptions(`${sector === 'residential' ? 'Residential' : 'Commercial'} Natural Gas (therms)`, 'Therms', true),
      latestYear,
      latestValue
    };
    
  } catch (error) {
    console.error(`Error fetching natural gas data for ${location}:`, error);
    throw error;
  }
}

export async function fetchNaturalGasStacked(location: string): Promise<EnergySeriesResult> {
  const [res, com] = await Promise.all([
    fetchNaturalGasSeries(location, 'residential'),
    fetchNaturalGasSeries(location, 'commercial')
  ]);
  const labels: number[] = Array.from(new Set([...(res.chartData.labels || []), ...(com.chartData.labels || [])])).sort();

  // Create maps for each series
  const resMap = new Map((res.chartData.labels || []).map((y: number, i: number) => [y, (res.chartData.datasets?.[0]?.data?.[i] ?? 0) as number]));
  const comMap = new Map((com.chartData.labels || []).map((y: number, i: number) => [y, (com.chartData.datasets?.[0]?.data?.[i] ?? 0) as number]));

  const resSeries = labels.map((y) => (resMap.has(y) ? (resMap.get(y) as number) : 0));
  const comSeries = labels.map((y) => (comMap.has(y) ? (comMap.get(y) as number) : 0));

  return {
    chartData: {
      labels,
      datasets: [
        { type: 'bar', label: 'Residential Natural Gas', data: resSeries, backgroundColor: sectorColors.residential.observed },
        { type: 'bar', label: 'Commercial Natural Gas', data: comSeries, backgroundColor: sectorColors.commercial.observed }
      ]
    },
    chartOptions: commonOptions('Total Natural Gas (therms)', 'Therms', true)
  };
}


